<script setup lang="ts">
import { responsibleHidingText, type LocationInput } from '@wilson/shared'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import LocationField from '../components/LocationField.vue'
import PublicHeader from '../components/PublicHeader.vue'
import { finderApi } from '../services/api'
import {
  acceptRecoveryFragment,
  getClaim,
  removeClaim,
  recoveryLink,
  type StoredClaim,
} from '../services/claims'

const route = useRoute()
const bottleId = computed(() => String(route.params.id))
const claim = ref<StoredClaim | null>(null)
const location = ref<LocationInput>()
const occurredAt = ref(new Date().toISOString().slice(0, 16))
const confirmed = ref(false)
const busy = ref(false)
const complete = ref(false)
const error = ref('')
const formStartedAt = Date.now()

onMounted(() => {
  claim.value = acceptRecoveryFragment(bottleId.value) ?? getClaim(bottleId.value)
})

async function copyLink() {
  if (claim.value)
    await navigator.clipboard.writeText(recoveryLink(bottleId.value, claim.value.token))
}

async function submit() {
  if (!claim.value || !location.value) return
  busy.value = true
  error.value = ''
  try {
    await finderApi(bottleId.value, claim.value.token, '/rehide', {
      occurredAt: new Date(occurredAt.value).toISOString(),
      location: location.value,
      responsibleHidingConfirmed: confirmed.value,
      website: '',
      formStartedAt,
    })
    removeClaim(bottleId.value)
    complete.value = true
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'The re-hide could not be recorded.'
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="public-page bottle-page-bg">
    <PublicHeader />
    <main class="narrow-main">
      <section v-if="complete" class="state-card success-state">
        <span class="state-icon">✦</span>
        <p class="eyebrow">Journey updated</p>
        <h1>The bottle is hidden again!</h1>
        <p>
          Your temporary keeper pass has been used and cannot be reused. The next finder will begin
          a new chapter.
        </p>
        <RouterLink class="button primary" :to="`/b/${bottleId}`"
          >See the updated journey</RouterLink
        >
      </section>
      <section v-else-if="!claim" class="state-card error-state">
        <span class="state-icon">!</span>
        <h1>Keeper pass needed</h1>
        <p>Open the private re-hide link saved when this bottle was marked found.</p>
        <RouterLink class="button secondary" :to="`/b/${bottleId}`">Back to bottle</RouterLink>
      </section>
      <template v-else>
        <div class="page-title">
          <p class="eyebrow">Complete the handoff</p>
          <h1>Where did the journey continue?</h1>
          <p>Record where you safely left the bottle. The exact location stays private.</p>
        </div>
        <p v-if="error" class="form-error banner" role="alert">{{ error }}</p>
        <form class="form-card" @submit.prevent="submit">
          <label
            >Dropped off date and time<input v-model="occurredAt" type="datetime-local" required
          /></label>
          <LocationField v-model="location" />
          <div class="responsibility-box">
            <span aria-hidden="true">🌿</span>
            <div>
              <strong>Hide responsibly</strong>
              <p>{{ responsibleHidingText }}</p>
            </div>
          </div>
          <label class="check-label"
            ><input v-model="confirmed" type="checkbox" required /><span
              >I have hidden this bottle somewhere safe and responsible.</span
            ></label
          >
          <div class="button-row">
            <button class="button primary" :disabled="busy || !location || !confirmed">
              {{ busy ? 'Saving the journey…' : 'Complete re-hide' }}</button
            ><button class="button ghost" type="button" @click="copyLink">
              Copy recovery link
            </button>
          </div>
        </form>
      </template>
    </main>
  </div>
</template>
